import { useState, useRef, useCallback } from 'react';
import { useTaskStore } from '../store/taskStore';

interface StreamGenerationOptions {
  action: string;
  payload: any;
  /** 后台化后“前往”按钮跳转的页面链接；不传则不显示“前往”按钮 */
  navigationUrl?: string;
  onChunk?: (chunk: string) => void;
  onDone?: (result: any) => void;
  onError?: (error: string) => void;
}

declare global {
  interface Window {
    UzySDK: {
      callSystemAgent: (
        action: string,
        payload: any,
        onChunk: (content: string) => void,
        onDone: (result: any) => void,
        options?: any
      ) => string;
      cancelSystemAgent: (requestId: string) => void;
      hideLoading: () => void;
      // 流式任务弹窗控制（每个流式调用对应一个 requestId）
      showTask: (requestId: string) => void;
      hideTask: (requestId: string) => void;
      destroyTask: (requestId: string) => void;
      getTask: (requestId: string) => any;
    };
  }
}

/**
 * AI 流式生成 Hook
 * 直接使用 SDK 弹窗，只有点击"后台运行"时才创建任务
 */
export function useStreamGeneration() {
  const [generating, setGenerating] = useState(false);
  const requestIdRef = useRef<string | null>(null);
  const { addTask, updateTask, addChunk } = useTaskStore();
  const chunksBufferRef = useRef<Array<{ content: string; timestamp: number }>>([]);
  const taskIdRef = useRef<string | null>(null);
  const optionsRef = useRef<StreamGenerationOptions | null>(null);

  const generate = useCallback(async (options: StreamGenerationOptions) => {
    const { action, payload, navigationUrl, onChunk, onDone, onError } = options;
    
    optionsRef.current = options;
    chunksBufferRef.current = [];
    taskIdRef.current = null;

    setGenerating(true);

    try {
      if (!window.UzySDK?.callSystemAgent) {
        throw new Error('SDK 未加载，请刷新页面重试');
      }

      // 自定义按钮：后台运行
      const customButtons = [{
        text: '后台运行',
        onClick: (requestId?: string) => {
          // 隐藏该任务专属弹窗（保活，可在消息中心“查看”恢复）
          const rid = requestId || requestIdRef.current || undefined;
          if (rid && window.UzySDK?.hideTask) {
            window.UzySDK.hideTask(rid);
          } else if (window.UzySDK?.hideLoading) {
            window.UzySDK.hideLoading();
          }

          // 幂等：若该任务已加入消息中心（查看后再次点击后台运行），仅隐藏即可，不重复新增
          if (taskIdRef.current) {
            return;
          }

          // 创建后台任务
          const tempTaskId = `task-${Date.now()}`;
          taskIdRef.current = tempTaskId;
          
          addTask({
            id: tempTaskId,
            taskId: '',
            resourceType: payload.key?.replace('generate_', '') || 'unknown',
            resourceId: payload.id || 0,
            resourceName: payload.resourceName || '生成中...',
            status: 'generating',
            progress: 0,
            statusText: 'AI 生成中...',
            createdAt: Date.now(),
            navigationUrl: navigationUrl,
            chunks: chunksBufferRef.current, // 已接收的 chunks
            onDone,
            requestId: rid,
          });
          
          // 标记为后台任务，并清除前台标记（前台显示由 SDK 弹窗负责，
          // activeTaskId 仅用于从消息中心列表排除前台项，后台化后必须清空，
          // 否则该任务因 t.id === activeTaskId 被过滤掉而不显示）
          const { markAsBackgrounded, setActiveTask } = useTaskStore.getState();
          markAsBackgrounded(tempTaskId);
          setActiveTask(null);
        }
      }];

      const requestId = window.UzySDK.callSystemAgent(
        action,
        payload,
        // onChunk
        (chunk: any) => {
          // 处理 task_created 消息
          if (typeof chunk === 'string' && chunk.startsWith('📋 任务已创建|')) {
            const realTaskId = chunk.split('|')[1];
            const newChunk = { content: '📋 任务已创建', timestamp: Date.now() };
            chunksBufferRef.current.push(newChunk);
            
            // 如果已经创建了后台任务，更新它
            if (taskIdRef.current) {
              addChunk(taskIdRef.current, newChunk);
              updateTask(taskIdRef.current, { taskId: realTaskId });
            }
            return;
          }
          
          // 缓存所有 chunks
          const newChunk = { content: chunk, timestamp: Date.now() };
          chunksBufferRef.current.push(newChunk);
          
          // 如果已经创建了后台任务，实时更新
          if (taskIdRef.current) {
            addChunk(taskIdRef.current, newChunk);
          }
          
          // 只有不带前缀的内容才调用 onChunk（用于收集内容）
          if (typeof chunk === 'string' && !chunk.startsWith('💭') && !chunk.startsWith('🔧') && !chunk.startsWith('📋')) {
            onChunk?.(chunk);
          }
        },
        // onDone
        (result: any) => {
          // 如果已经创建了后台任务，更新状态
          if (taskIdRef.current) {
            updateTask(taskIdRef.current, { status: 'done', statusText: '生成完成' });
          } else {
            // 未后台化的前台任务：完成后销毁其专属弹窗 DOM
            const rid = requestIdRef.current;
            if (rid && window.UzySDK?.destroyTask) {
              window.UzySDK.destroyTask(rid);
            }
          }
          
          setGenerating(false);
          requestIdRef.current = null;
          onDone?.(result);
        },
        { customButtons }
      );

      requestIdRef.current = requestId;
    } catch (error: any) {
      // 如果已经创建了后台任务，标记为失败
      if (taskIdRef.current) {
        updateTask(taskIdRef.current, { 
          status: 'error', 
          error: error.message, 
          statusText: '生成失败' 
        });
      }
      
      setGenerating(false);
      requestIdRef.current = null;
      onError?.(error.message || '生成失败');
    }
  }, [addTask, updateTask, addChunk]);

  const cancel = useCallback(() => {
    if (requestIdRef.current && window.UzySDK?.cancelSystemAgent) {
      window.UzySDK.cancelSystemAgent(requestIdRef.current);
      setGenerating(false);
      requestIdRef.current = null;
    }
  }, []);

  return { generating, generate, cancel };
}
